import { Capacitor } from "@capacitor/core";
import { useIsMobile } from "@/hooks/use-mobile";
import { QrCode, Smartphone } from "lucide-react";
import { useEffect, useState } from "react";
import { canonicalUrl } from "@/config/site";
import { withUtm, playStoreUrlWithUtm, appStoreUrlWithUtm } from "@/lib/utm";

// Store URLs - Update APP_STORE_URL when you have the App Store ID
// Store links are campaign-tagged so app installs are attributed to the website
// instead of collapsing into GA4's "(direct) / (none)" bucket.
const PLAY_STORE_URL = playStoreUrlWithUtm(
  "https://play.google.com/store/apps/details?id=com.brrgr.mobile",
  { source: "web", medium: "app_download", campaign: "site_download_section" },
);
const APP_STORE_URL = appStoreUrlWithUtm(
  "https://apps.apple.com/us/app/brrgr/id6749858763",
  "web_download_section",
);

type Platform = "ios" | "android" | "desktop" | "native";

const detectUserPlatform = (): Platform => {
  // Check if running in native app
  if (Capacitor.isNativePlatform()) {
    return "native";
  }

  const userAgent = navigator.userAgent.toLowerCase();
  
  // Check for iOS devices
  if (/iphone|ipad|ipod/.test(userAgent)) {
    return "ios";
  }
  
  // Check for Android devices
  if (/android/.test(userAgent)) {
    return "android";
  }
  
  return "desktop";
};

// QR Code component using self-hosted static image
const QRCode = ({ size = 180 }: { size?: number }) => {
  return (
    <div className="bg-white p-3 rounded-xl shadow-soft inline-block">
      <img 
        src="/qr-download.png" 
        alt="Scan to download BRRGR app" 
        width={size} 
        height={size}
        className="block"
        loading="eager"
      />
    </div>
  );
};

// Official App Store badge - uses <a target="_blank"> which nativeLinks.ts handles
const AppStoreBadge = ({ className }: { className?: string }) => (
  <a 
    href={APP_STORE_URL} 
    target="_blank" 
    rel="noopener noreferrer"
    className={`inline-block transition-transform hover:scale-105 ${className}`}
    aria-label="Download on the App Store"
  >
    <img 
      src="/badges/app-store.svg"
      alt="Download on the App Store"
      className="h-[52px]"
      loading="eager"
    />
  </a>
);

// Official Google Play badge - uses <a target="_blank"> which nativeLinks.ts handles
const PlayStoreBadge = ({ className }: { className?: string }) => (
  <a 
    href={PLAY_STORE_URL} 
    target="_blank" 
    rel="noopener noreferrer"
    className={`inline-block transition-transform hover:scale-105 ${className}`}
    aria-label="Get it on Google Play"
  >
    <img 
      src="/badges/google-play.png"
      alt="Get it on Google Play"
      width={646}
      height={250}
      className="h-[76px] w-auto -my-3"
      loading="eager"
    />
  </a>
);


interface AppDownloadSectionProps {
  className?: string;
  hideHeading?: boolean;
}

const AppDownloadSection = ({ className, hideHeading = false }: AppDownloadSectionProps) => {
  const [platform, setPlatform] = useState<Platform>("desktop");
  const isMobile = useIsMobile();

  useEffect(() => {
    setPlatform(detectUserPlatform());
  }, []);

  // Don't show download section in native app - user already has it
  if (platform === "native") {
    return null;
  }

  // Mobile web: Show the relevant store badge
  if (platform === "ios" || platform === "android") {
    return (
      <div className={`flex flex-col items-center text-center ${className}`}>
        {!hideHeading && (
          <div className="mb-4">
            <Smartphone className="h-8 w-8 text-primary mx-auto mb-2" />
            <h2 className="text-xl md:text-2xl font-bold text-primary">Get the BRRGR app</h2>
            <p className="text-base text-foreground/70 mt-1">
              Track burgers on the go with the free app
            </p>
          </div>
        )}
        
        {platform === "ios" ? (
          <AppStoreBadge />
        ) : (
          <PlayStoreBadge />
        )}
        
        <p className="text-xs text-muted-foreground mt-4">
          Or continue in this browser
        </p>
      </div>
    );
  }

  // Desktop: Show QR code + both badges
  // QR points to /download which auto-redirects to the correct store
  const smartLinkUrl = withUtm(canonicalUrl("/download"), {
    source: "qr_code",
    medium: "app_download",
    campaign: "desktop_qr",
  });
  
  return (
    <div className={`flex flex-col items-center text-center ${className}`}>
      {!hideHeading && (
        <div className="mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-primary">Get the BRRGR app</h2>
          <p className="text-base text-foreground/70 mt-2">
            Scan the QR code to download
          </p>
        </div>
      )}
      
      <QRCode size={160} />
      
      <div className="flex items-center gap-2 mt-6">
        <AppStoreBadge />
        <PlayStoreBadge />
      </div>
    </div>
  );
};

export default AppDownloadSection;
